<script lang="ts">
  import { goto } from '$app/navigation';
  import { page } from '$app/stores';

  import { get } from 'svelte/store';

  import { Container } from '@shared/ui/container';
  import { Heading } from '@shared/ui/heading';
  import { is, serializeSearchParams } from '@shared/utils';

  import { NewsApi } from '@entities/news';
  import type { NewsFromManyResponse } from '@entities/news/types';

  import { Pagination } from '@features/pagination';

  import { NEWS_PER_PAGE } from '../configs';
  import List from './news-list.svelte';

  export let news: NewsFromManyResponse['data'];
  export let totalCount: number;
  export let loadMore: number;
  export let pageNumber: number;

  const newsApi = new NewsApi(fetch);
  const limit = NEWS_PER_PAGE;

  let container: HTMLElement | undefined;
  let loading = false;
  let prevPage = pageNumber;

  async function requestRedirect(params: { page: number; loadMore?: number; invalidateAll?: boolean }) {
    const { url } = get(page);

    const newPage = params?.page || 1;
    const isSamePage = params.page === prevPage;

    if (!params.loadMore) {
      $page.url.searchParams.delete('loadMore');
    }

    const searchParamsString = serializeSearchParams(url.searchParams);

    if (isSamePage) {
      await goto(searchParamsString, {
        noScroll: isSamePage,
        replaceState: true,
        invalidateAll: params.invalidateAll ?? false,
      });
    } else {
      const newUrl = `${is(newPage !== 1, `/news/page-${newPage}`, `/news`)}${searchParamsString}`;

      prevPage = pageNumber;

      await goto(newUrl, { invalidateAll: params.invalidateAll ?? false, noScroll: true });
    }
  }

  async function handleLoadMore(params: { page: number; loadMore?: number }) {
    const { url } = get(page);

    url.searchParams.set('loadMore', `${params.loadMore}`);

    const searchParams = configureSearchParams(params);

    const someReceivedNews = (await handleLoadNews(searchParams)).data;

    news = [...news, ...someReceivedNews];

    await goto(`?${url.searchParams.toString()}`, {
      noScroll: true,
      replaceState: true,
      invalidateAll: false,
    });
  }

  async function handleLoadNews(params: URLSearchParams) {
    const someReceivedNews = await newsApi.readMany(params);

    return someReceivedNews;
  }

  function configureSearchParams(params: { page: number; loadMore?: number }) {
    const pageParams = {
      offset: `${limit * (params.page + (params.loadMore ?? 0) - 1)}`,
      limit: `${limit}`,
      column: 'custom_publication_date',
      order: 'desc',
    };

    const searchParams = new URLSearchParams(pageParams);

    return searchParams;
  }
</script>

<div class="wrapper-container" bind:this="{container}">
  <Heading
    title="UFDA's Articles: Insights into Ukraine's Art and Culture"
    tag="h1"
    counter="{totalCount}"
    titleClass="text-3xl lg:text-5xl"
    dividePosition="none"
  />

  <Container containerClass="pb-8 md:pb-12">
    <p class="mb-9 max-w-[888px] font-medium">
      Explore updates on Ukraine’s art scene, preservation efforts, and the role of digital innovation in safeguarding
      cultural heritage. Stay connected to Ukraine’s artistic legacy.
    </p>

    {#if news?.length}
      <Pagination
        isFullWidth
        containerToScroll="{container}"
        pageSize="{limit}"
        requestFunction="{requestRedirect}"
        loadMoreFunction="{handleLoadMore}"
        bind:currentPage="{pageNumber}"
        bind:loadMore
        bind:totalDataCount="{totalCount}"
        bind:loading
      >
        <List {news} />
      </Pagination>
    {:else}
      <p class="my-12 flex h-[50vh] items-center justify-center text-center text-5xl lg:my-14">No articles found.</p>
    {/if}
  </Container>
</div>
